import { createFileRoute } from '@tanstack/react-router';
import type { IconName } from 'lucide-react/dynamic';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Content } from '@/components/ui/content';
import {
  ActionsButton,
  DataTable,
  type InferFetcherParams,
} from '@/components/ui/data-table';
import { ContentLoader } from '@/components/ui/loaders';
import { SearchToolbar } from '@/components/ui/search-toolbar';
import { useIsMobile } from '@/hooks/use-mobile';
import { useRouter } from '@/hooks/use-router';
import { apiClient } from '@/lib/api-client';
import { meta } from '@/lib/meta';
import { layoutToColumns, layoutToFilters } from '@/lib/screen-renderer';
import { useIsAuthenticated } from '@/services/auth';
import { useDynamicDelete, useDynamicList } from '@/services/dynamic-resource';
import { useScreenByResource, useScreenPermissions } from '@/services/screens';
import { ResetPasswordButton } from './+components/reset-password-button';

export const Route = createFileRoute('/_app/$resource/')({
  head: () => meta({ label: 'Listagem' }),
  component: RouteComponent,
});

function RouteComponent() {
  const { resource } = Route.useParams();
  const isMobile = useIsMobile();
  const { push } = useRouter();

  const { data: screen, isLoading: loadingScreen } = useScreenByResource(resource);
  const { canCreate, canRead, canUpdate, canDelete } = useScreenPermissions(screen);
  const { user: currentUser } = useIsAuthenticated();

  const { mutateAsync: remove } = useDynamicDelete(resource);
  const useList = (params: InferFetcherParams<any>) =>
    useDynamicList(resource, params as any);

  const [search, setSearch] = useState<Record<string, any>>({});

  const handleAdd = () => push(`/${resource}/create`);
  const handleEdit = (id: string) => push(`/${resource}/${id}`);
  const handleView = (id: string) => push(`/${resource}/${id}/view`);
  const handleRemove = async (id: string) => await remove(id);

  /** Só pra customers: avisa quantos pedidos serão excluídos junto (cascade) antes de confirmar. */
  const getRemoveDescription =
    resource === 'customers'
      ? async (id: string) => {
          try {
            const res = await apiClient.sales.get({
              query: { customerId: id, perPage: 1 } as any,
            });
            const total = (res.data as any)?.pagination?.total ?? 0;
            if (total === 0) return undefined;
            return `Este cliente tem ${total} pedido${total > 1 ? 's' : ''} vinculado${total > 1 ? 's' : ''}. ${total > 1 ? 'Eles serão excluídos' : 'Ele será excluído'} junto. Deseja continuar?`;
          } catch (error) {
            console.error('Falha ao checar pedidos vinculados ao cliente:', error);
            return undefined;
          }
        }
      : undefined;
  const handleDoubleClick = (row: any) =>
    canUpdate ? handleEdit(row.id) : canRead ? handleView(row.id) : undefined;

  if (loadingScreen || !screen) return <ContentLoader show />;

  const columns = layoutToColumns(
    [
      { name: 'id', label: 'Cód.', type: 'string', sortable: true },
      ...screen.layout.columns,
      { name: 'createdAt', label: 'Criado em', type: 'date', sortable: true },
      { name: 'updatedAt', label: 'Atualizado em', type: 'date', sortable: true },
    ],
    resource,
  );
  const filters = layoutToFilters(screen.layout.filters);

  columns[isMobile ? 'unshift' : 'push']({
    id: 'actions',
    header: () => <div className="flex items-center justify-end">Ações</div>,
    enableHiding: false,
    cell: ({ row }: any) => {
      // O próprio usuário não pode editar/excluir/resetar a própria senha por aqui —
      // edição de nome/foto/senha vai pelo Editar Perfil, e ninguém deveria conseguir
      // se apagar sozinho.
      const isSelf = resource === 'users' && row.original.id === currentUser?.id;

      return (
        <div className="flex items-center justify-end gap-1">
          {resource === 'users' && currentUser?.role === 'admin' && !isSelf && (
            <ResetPasswordButton userId={row.original.id} />
          )}
          <ActionsButton
            key={row.id}
            id={row.original.id}
            edit={canUpdate && !isSelf}
            view={canRead}
            remove={canDelete && !isSelf}
            onEdit={handleEdit}
            onView={handleView}
            onRemove={handleRemove}
            getRemoveDescription={getRemoveDescription}
          />
        </div>
      );
    },
    meta: { sticky: isMobile ? 'left' : 'right' },
  });

  return (
    <Content
      breadcrumbs={{
        icon: screen.icon as IconName,
        title: screen.pluralLabel,
        action: canCreate && (
          <Button variant="success" onClick={handleAdd} icon="plus">
            Adicionar
          </Button>
        ),
        items: [{ label: 'Listagem', active: true }],
      }}
    >
      <SearchToolbar filters={filters} onSearch={setSearch} />
      <DataTable
        columns={columns}
        useFetcher={useList as any}
        search={search}
        onRowDoubleClick={handleDoubleClick}
      />
    </Content>
  );
}
